import React from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Award, Users, MapPin } from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Quality Excellence',
      description: 'We use only premium products and professional-grade equipment to deliver exceptional results.',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Customer-Focused',
      description: 'Your satisfaction is our priority. We tailor our services to meet your specific needs.',
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: 'Mobile Convenience',
      description: 'We bring our expertise to you, saving you time and providing service at your location.',
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Attention to Detail',
      description: 'Every vehicle receives meticulous care and attention from our experienced technicians.',
    },
  ];

  const whyChooseUs = [
    'Mobile service at your location',
    'Professional equipment and products',
    'Fast turnaround times',
    'Personalized pricing for every vehicle',
    'Visible, measurable results',
    'Experience with all vehicle types',
    'Flexible scheduling options',
    'High attention to detail',
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-black">
      <SEO 
        title="About Us – ONYXA Car Detailing"
        description="Learn about ONYXA Car Polishing & Detailing. Professional mobile detailing services with years of experience in paint correction, ceramic coating, and premium car care."
        canonicalPath="/about"
      />
      <Navigation />

      {/* Hero Section */}
      <section data-testid="about-hero" className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-red-600 rounded-full filter blur-[150px]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 
            data-testid="about-hero-title"
            className="text-5xl lg:text-6xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            About <span className="text-red-500">ONYXA</span>
          </h1>
          <p data-testid="about-hero-subtitle" className="text-lg text-gray-300 max-w-3xl mx-auto">
            Professional mobile car detailing serving the tri-state area — bringing premium results directly to you
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section data-testid="about-story-section" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-8 md:p-12">
            <h2 
              data-testid="about-story-title"
              className="text-3xl lg:text-4xl font-bold mb-6 text-center"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Our Story
            </h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                <strong className="text-red-500">ONYXA</strong> was created to bring high-quality car polishing & detailing directly to customers throughout the tri-state area. We understand that your time is valuable, and taking your vehicle to a detailing shop isn't always convenient.
              </p>
              <p>
                That's why we developed a fully mobile service that delivers professional-grade results at your location. Whether you're at home, at work, or anywhere across New York City (including Brooklyn, Queens, Manhattan, Staten Island, and the Bronx), Long Island, Westchester, Connecticut, or New Jersey — we come to you.
              </p>
              <p>
                We work with all types of vehicles — from daily drivers and family cars to luxury automobiles. Every vehicle receives the same level of care and attention to detail, ensuring consistent, exceptional results across our entire service area.
              </p>
              <p>
                Our mission is simple: <strong>make premium detailing convenient, affordable, and mobile.</strong> We believe everyone deserves a vehicle that looks and feels like new, without the hassle of traditional detailing services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="about-values-section" className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              data-testid="about-values-title"
              className="text-4xl lg:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Our Values
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div data-testid="about-values-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} data-testid={`value-item-${index}`} className="glass-card p-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-600 to-red-800 rounded-2xl mb-4">
                  <div className="text-white">{value.icon}</div>
                </div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-gray-400 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-testid="about-why-choose-section" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              data-testid="about-why-title"
              className="text-4xl lg:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Why Choose ONYXA?
            </h2>
            <p className="text-gray-400">
              What sets us apart from traditional detailing services
            </p>
          </div>

          <div data-testid="about-why-list" className="glass-card p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {whyChooseUs.map((item, index) => (
                <div key={index} data-testid={`why-item-${index}`} className="flex items-center space-x-3">
                  <CheckCircle className="text-red-500 flex-shrink-0" size={24} />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section data-testid="about-target-section" className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            data-testid="about-target-title"
            className="text-4xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Who We Serve
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            ONYXA serves car owners throughout New York City, Long Island, Westchester, Connecticut, and New Jersey. Perfect for:
          </p>
          <div data-testid="about-target-list" className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold mb-2 text-red-500">NYC Residents</h3>
              <p className="text-gray-400 text-sm">Brooklyn, Queens, Manhattan, Bronx, Staten Island — mobile service at your location</p>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold mb-2 text-red-500">Long Island Homeowners</h3>
              <p className="text-gray-400 text-sm">Nassau & Suffolk Counties — convenient driveway detailing</p>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold mb-2 text-red-500">Busy Professionals</h3>
              <p className="text-gray-400 text-sm">Westchester, CT, NJ — service at home or office</p>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold mb-2 text-red-500">All Vehicle Types</h3>
              <p className="text-gray-400 text-sm">Daily drivers, family cars, luxury vehicles, rideshare cars</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="about-cta-section" className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center glass-card p-12 rounded-3xl">
          <h2 
            data-testid="about-cta-title"
            className="text-4xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Experience the ONYXA Difference
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Ready to transform your vehicle? Let's get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="about-cta-book-btn"
              onClick={() => navigate('/booking')}
              className="btn-primary"
            >
              Book Appointment
            </button>
            <button
              data-testid="about-cta-contact-btn"
              onClick={() => navigate('/contact')}
              className="btn-secondary"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
